/**
 * VrPointers — every input source the headset offers, as one kind of pointer (XR P2 "VR UI / UX", 2026-09-28).
 *
 * Controllers (the trigger), tracked hands (a pinch is `select`; the ray is damped), and Vision Pro's
 * transient-pointer (a gaze-and-pinch ray that exists only for the gesture) all arrive through three's XRManager
 * slots and fire the SAME session events, so they share one path:
 *
 *   selectstart → the target is chosen   (a pinch freezes what it was on: the pinch itself moves the hand)
 *   select      → the click              (a controller clicks only what it is STILL on at release)
 *   selectend   → the press is over
 *
 * THE FOUR-SLOT LAW. three assigns a new input source to the first free controller slot among the slots that EXIST
 * and ignores the rest ("If all controllers do currently receive input we ignore new ones"). Vision Pro keeps both
 * hands connected (slots 0, 1) and adds each transient pointer on top — with only two slots claimed, the pinch that
 * should open the menu never reaches the page. Four slots are claimed at init, before any session.
 *
 * Everything is RIG-LOCAL: the controller, grip and hand spaces are children of the avatar rig (the camera's parent,
 * the frame three composes every XR pose in), and so are the drawn rays and cursors — a damped ray is drawn where it
 * is DAMPED, not where the raw controller points.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { withLineNormals } from '../../lib/line_geometry.js';
import {
  RayDamper, pointerKindOf, dampedKind, resolveSelect, pressAtSelect, rotate, add, scale,
  type PointerKind, type V3, type Q4,
} from '../../lib/xr_ui_input.js';

export const POINTER_SLOTS = 4;
/** The ray drawn when it touches nothing (short — a laser across the whole world is noise). */
const IDLE_RAY_M = 0.9;

export interface Pointer {
  slot: number;
  ctl: THREE.Object3D;
  grip: THREE.Object3D;
  hand: THREE.Object3D;
  source: XRInputSource | null;
  kind: PointerKind;
  handedness: XRHandedness;
  damper: RayDamper;
  /** The (damped) ray this frame, rig-local. */
  o: V3;
  d: V3;
  valid: boolean;
  hover: string | null;
  hoverT: number;
  pressing: boolean;
  pressTarget: string | null;
  clicks: number;
  lastClick: string | null;
  line: THREE.Line;
  cursor: THREE.Mesh;
}

export interface PickHit { target: string; t: number; point: V3 }

export interface PointerHost {
  /** The nearest UI target along a rig-local ray, or null. */
  pick(o: V3, d: V3, p: Pointer): PickHit | null;
  /** A completed click on `target` by pointer `p`. */
  click(p: Pointer, target: string): void;
}

type Evt = { data?: XRInputSource };
interface Dispatcher { addEventListener(type: string, fn: (e: Evt) => void): void }
export interface XrSlots {
  getController(i: number): THREE.Object3D;
  getControllerGrip(i: number): THREE.Object3D;
  getHand(i: number): THREE.Object3D;
}

const UNIT_NEG_Z = new THREE.Vector3(0, 0, -1);

export class VrPointers {
  readonly pointers: Pointer[] = [];
  private rig: THREE.Object3D | null = null;
  private readonly lineMat: THREE.LineBasicMaterial;
  private readonly cursorMat: THREE.MeshBasicMaterial;
  private readonly lineGeo: THREE.BufferGeometry;
  private readonly cursorGeo: THREE.SphereGeometry;
  private readonly tmpDir = new THREE.Vector3();

  constructor(private readonly host: PointerHost) {
    this.lineGeo = withLineNormals(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1)]));
    this.lineMat = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.75, depthTest: false, depthWrite: false, toneMapped: false });
    this.cursorGeo = new THREE.SphereGeometry(0.0055, 12, 8);
    this.cursorMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false, toneMapped: false });
    markMrtOverlay(this.lineMat);
    markMrtOverlay(this.cursorMat);
  }

  /** Claim the four slots (idempotent). Called at init, before any session exists — THE FOUR-SLOT LAW. */
  claim(xrm: XrSlots): void {
    if (this.pointers.length) return;
    for (let slot = 0; slot < POINTER_SLOTS; slot++) {
      const ctl = xrm.getController(slot);
      const grip = xrm.getControllerGrip(slot);
      const hand = xrm.getHand(slot); // joints populate only for a hand whose space was asked for before it connected
      const line = new THREE.Line(this.lineGeo, this.lineMat);
      line.name = `vr-ray-${slot}`;
      line.renderOrder = 997;
      line.frustumCulled = false;
      line.visible = false;
      const cursor = new THREE.Mesh(this.cursorGeo, this.cursorMat);
      cursor.name = `vr-cursor-${slot}`;
      cursor.renderOrder = 1003;
      cursor.frustumCulled = false;
      cursor.visible = false;
      const p: Pointer = {
        slot, ctl, grip, hand, source: null, kind: 'controller', handedness: 'none', damper: new RayDamper(),
        o: { x: 0, y: 0, z: 0 }, d: { x: 0, y: 0, z: -1 }, valid: false, hover: null, hoverT: 0,
        pressing: false, pressTarget: null, clicks: 0, lastClick: null, line, cursor,
      };
      const on = (type: string, fn: (e: Evt) => void): void => (ctl as unknown as Dispatcher).addEventListener(type, fn);
      on('connected', (e) => {
        const src = e.data ?? null;
        p.source = src;
        p.kind = pointerKindOf(src?.targetRayMode, !!src?.hand);
        p.handedness = src?.handedness ?? 'none';
        p.damper.reset();
        p.pressing = false;
        p.pressTarget = null;
      });
      on('disconnected', () => {
        p.source = null;
        p.valid = false;
        p.hover = null;
        p.pressing = false;
        p.pressTarget = null;
        p.line.visible = false;
        p.cursor.visible = false;
      });
      on('selectstart', () => {
        p.pressing = true;
        // A pinch chooses what it was ON before the pinch moved the hand (the damped hover); a controller and a
        // transient pointer choose from the pose three just updated for this very event.
        p.pressTarget = p.kind === 'hand' ? (p.hover ?? this.freshPick(p)) : this.freshPick(p);
      });
      on('select', () => {
        const fresh = this.freshPick(p);
        const press = pressAtSelect(p.kind, p.pressing, p.pressTarget, p.hover, fresh);
        const release = p.kind === 'controller' ? fresh : press;
        const target = resolveSelect(p.kind, press, release);
        if (target) {
          p.clicks++;
          p.lastClick = target;
          try { this.host.click(p, target); } catch (err) { console.warn('[XR UI] click failed', err); }
        }
      });
      on('selectend', () => { p.pressing = false; p.pressTarget = null; });
      this.pointers.push(p);
    }
  }

  /** Parent the slots, rays and cursors to the rig (re-parents when the rig is rebuilt — a world hop). */
  attach(rig: THREE.Object3D | null): void {
    if (!rig || rig === this.rig) return;
    for (const p of this.pointers) rig.add(p.ctl, p.grip, p.hand, p.line, p.cursor);
    this.rig = rig;
  }

  /** The raw rig-local ray of a slot (three updated the space for this frame / this event). */
  rawRay(p: Pointer): { o: V3; d: V3 } {
    const q = p.ctl.quaternion;
    const qq: Q4 = { x: q.x, y: q.y, z: q.z, w: q.w };
    const pos = p.ctl.position;
    return { o: { x: pos.x, y: pos.y, z: pos.z }, d: rotate({ x: 0, y: 0, z: -1 }, qq) };
  }

  private freshPick(p: Pointer): string | null {
    if (!p.source) return null;
    const r = this.rawRay(p);
    return this.host.pick(r.o, r.d, p)?.target ?? null;
  }

  /** Per frame: the damped rays, the hover targets, and the drawn rays / cursors (`show` decides per pointer). */
  update(dt: number, show: (p: Pointer, hit: PickHit | null) => boolean): void {
    for (const p of this.pointers) {
      p.valid = !!p.source && p.ctl.visible && (p.kind === 'controller' || p.kind === 'hand' || p.kind === 'transient');
      if (!p.valid) {
        p.hover = null;
        p.line.visible = false;
        p.cursor.visible = false;
        continue;
      }
      const raw = this.rawRay(p);
      if (dampedKind(p.kind)) {
        const s = p.damper.step(raw.o, raw.d, dt);
        p.o = s.o;
        p.d = s.d;
      } else {
        p.o = raw.o;
        p.d = raw.d;
      }
      const hit = this.host.pick(p.o, p.d, p);
      p.hover = hit?.target ?? null;
      p.hoverT = hit?.t ?? 0;
      // A transient pointer has no ray between gestures; draw it only while pinched.
      const draw = show(p, hit) && (p.kind !== 'transient' || p.pressing);
      p.line.visible = draw;
      p.cursor.visible = draw && !!hit;
      if (draw) {
        p.line.position.set(p.o.x, p.o.y, p.o.z);
        this.tmpDir.set(p.d.x, p.d.y, p.d.z);
        p.line.quaternion.setFromUnitVectors(UNIT_NEG_Z, this.tmpDir);
        p.line.scale.set(1, 1, hit ? Math.max(0.01, hit.t) : IDLE_RAY_M);
        if (hit) {
          const at = hit.point ?? add(p.o, scale(p.d, hit.t));
          p.cursor.position.set(at.x, at.y, at.z);
        }
      }
    }
  }

  /** The hovered targets this frame (every pointer). */
  hovers(): Set<string> {
    const s = new Set<string>();
    for (const p of this.pointers) if (p.valid && p.hover) s.add(p.hover);
    return s;
  }

  /** A tracked hand's index fingertip, rig-local (the POKE), or null. */
  fingertip(p: Pointer): V3 | null {
    if (p.kind !== 'hand' || !p.source) return null;
    const joints = (p.hand as unknown as { joints?: Record<string, THREE.Object3D | undefined> }).joints;
    const tip = joints?.['index-finger-tip'];
    if (!tip || !tip.visible) return null;
    return { x: tip.position.x, y: tip.position.y, z: tip.position.z };
  }

  /** The grip pose of the pointer on `hand` (the wrist menu's watch check), rig-local. */
  gripOf(hand: 'left' | 'right'): { pos: V3; rot: Q4; kind: PointerKind } | null {
    for (const p of this.pointers) {
      if (!p.source || p.handedness !== hand || p.kind === 'transient' || !p.grip.visible) continue;
      const g = p.grip;
      return { pos: { x: g.position.x, y: g.position.y, z: g.position.z }, rot: { x: g.quaternion.x, y: g.quaternion.y, z: g.quaternion.z, w: g.quaternion.w }, kind: p.kind };
    }
    return null;
  }

  /** A tracked hand's 'wrist' joint, rig-local — the watch check for hands (three never updates their grip). */
  wristOf(hand: 'left' | 'right'): { pos: V3; rot: Q4 } | null {
    for (const p of this.pointers) {
      if (!p.source || p.handedness !== hand || p.kind !== 'hand') continue;
      const joints = (p.hand as unknown as { joints?: Record<string, THREE.Object3D | undefined> }).joints;
      const w = joints?.['wrist'];
      if (!w || !w.visible) return null;
      return { pos: { x: w.position.x, y: w.position.y, z: w.position.z }, rot: { x: w.quaternion.x, y: w.quaternion.y, z: w.quaternion.z, w: w.quaternion.w } };
    }
    return null;
  }

  /** The probe's view of every slot. */
  snapshot(): Array<Record<string, unknown>> {
    return this.pointers.map((p) => ({
      slot: p.slot, kind: p.kind, handedness: p.handedness, valid: p.valid, connected: !!p.source,
      targetRayMode: p.source?.targetRayMode ?? null, hover: p.hover, pressing: p.pressing, clicks: p.clicks, lastClick: p.lastClick,
      o: p.o, d: p.d,
      ctlPos: { x: p.ctl.position.x, y: p.ctl.position.y, z: p.ctl.position.z },
      ctlQuat: { x: p.ctl.quaternion.x, y: p.ctl.quaternion.y, z: p.ctl.quaternion.z, w: p.ctl.quaternion.w },
      gripVisible: p.grip.visible,
      gripPos: { x: p.grip.position.x, y: p.grip.position.y, z: p.grip.position.z },
      gripQuat: { x: p.grip.quaternion.x, y: p.grip.quaternion.y, z: p.grip.quaternion.z, w: p.grip.quaternion.w },
      rayVisible: p.line.visible, parent: p.ctl.parent?.name ?? null, tip: this.fingertip(p),
      wristJoint: p.handedness === 'left' || p.handedness === 'right' ? this.wristOf(p.handedness) : null,
    }));
  }

  hideVisuals(): void {
    for (const p of this.pointers) { p.line.visible = false; p.cursor.visible = false; p.hover = null; }
  }

  dispose(): void {
    for (const p of this.pointers) { p.line.removeFromParent(); p.cursor.removeFromParent(); }
    this.lineGeo.dispose();
    this.lineMat.dispose();
    this.cursorGeo.dispose();
    this.cursorMat.dispose();
  }
}
